import { initializeTabs } from '../tabs/tabManager.js';
import { setupEventListeners } from './events.js';
import { handleInitializationError } from './errorHandler.js';
import OnboardingManager from '../onboarding/OnboardingManager.js';
import rootPersonStore from '../common/stores/rootPersonStore.js';
import authStore from '../common/stores/authStore.js';
import contextStore from '../common/stores/contextStore.js';
import gedcomDataStore from '../gedcom/stores/gedcomDataStore.js';
import overlayManager from '../utils/OverlayManager.js';
// Removed obsolete dataSizeAnalyzer import
import benchmarkManager from '../utils/benchmarkManager.js';
import aiNotesIntegration from '../services/aiNotesIntegration.js';
import treeAutoSaveService from '../services/treeAutoSaveService.js';
import backgroundTaskStore from '../common/stores/backgroundTaskStore.js';
import { initializeBackgroundTaskIndicator } from '../ui/backgroundTaskIndicator.js';
import { logger } from '../utils/debug/logger.js';

/**
 * Point d'entrée principal de l'application
 * Combine les fonctionnalités de app.js et setup.js en un seul fichier
 */

export async function initializeApplication() {
    // Configurer le gestionnaire d'erreurs global
    window.onerror = handleInitializationError;
    
    try {
        // Afficher l'overlay pendant l'initialisation
        overlayManager.showGlobal('Initialisation de l\'application...');
        
        logger.initialization.bootstrap.info('🚀 Initialisation de l\'application');
        performance.mark('app-init-start');
        
        // Configurer la gestion avant déchargement de la page
        setupBeforeUnload();
        
        // Attendre que le DOM soit chargé
        await initializeDOMContent();

        logger.initialization.bootstrap.info('🔒 Initialisation du système d\'authentification...');
        
        // Utiliser le contextStore pour le contexte d'exécution
        logger.initialization.bootstrap.info(`📌 Application ${contextStore.mode}`);
        
        // Rendre les stores accessibles pour le débogage
        window.contextStore = contextStore;
        window.rootPersonStore = rootPersonStore;
        window.authStore = authStore;
        window.gedcomDataStore = gedcomDataStore;
        window.aiNotesIntegration = aiNotesIntegration;
        window.backgroundTaskStore = backgroundTaskStore;
        aiNotesIntegration.setupExtractionConsent();
        treeAutoSaveService.initialize();

        // Removed obsolete analyzeCurrentStores function
        window.benchmarkManager = benchmarkManager;
        
        // Exposer les fonctions d'optimisation pour les tests
        const { expandEvent } = await import('../utils/eventOptimizer.js');
        const { generateEventDescription } = await import('../utils/eventDescriptionGenerator.js');
        window.expandEvent = expandEvent;
        window.generateEventDescription = generateEventDescription;
        
        // Initialiser le PersonDetailsManager global
        const PersonDetailsManager = await import('../common/PersonDetailsManager.js');
        window.PersonDetailsManager = PersonDetailsManager.default;
        
        // Initialiser l'authentification en premier (nécessaire pour autres composants)
        await authStore.checkAuthentication();

        logger.initialization.bootstrap.info('🚀 Démarrage de l\'initialisation parallèle...');
        
        // Mesurer le temps d'initialisation parallèle
        // PHASE 1: Initialisation critique (tabs uniquement)
        performance.mark('critical-init-start');
        await initializeTabs();
        performance.mark('critical-init-end');
        performance.measure('critical-initialization', 'critical-init-start', 'critical-init-end');
        
        const criticalTime = performance.getEntriesByName('critical-initialization')[0];
        logger.initialization.bootstrap.info(`⚡ Phase critique (tabs) terminée en ${criticalTime.duration.toFixed(2)}ms`);
        
        // L'application est maintenant utilisable - masquer l'overlay
        hideOverlay();
        window.isReady = true;
        overlayManager.hideGlobal();
        
        // PHASE 2: Initialisation secondaire en parallèle (non-bloquante)
        logger.initialization.bootstrap.info('🚀 Démarrage de la phase secondaire en arrière-plan');
        Promise.allSettled([
            initializeTomSelect(),
            initializeOnboarding(),
            initializeEventListeners(),
            initializeFanHeroManager(),
            initializeHeaderStateManager(),
            initializeDebugPanel(),
            initializePersonDetailsTests(),
            handleUrlParameters(),
            initializeBackgroundTaskUI(),
            initializeSessionTracking(),
            initializeSessionRestore()
        ]).then(results => {
            const successCount = results.filter(r => r.status === 'fulfilled').length;
            const failureCount = results.filter(r => r.status === 'rejected').length;
            logger.initialization.bootstrap.info(`✅ Phase secondaire terminée: ${successCount} succès, ${failureCount} échecs`);
            
            // PHASE 3: Préchargement des modules (si activé)
            if (typeof requestIdleCallback !== 'undefined') {
                requestIdleCallback(() => {
                    logger.initialization.bootstrap.debug('🔄 Préchargement en arrière-plan disponible pour activation future');
                    // Pour activer le préchargement, décommenter ces lignes :
                    // preloadCriticalModules().catch(error => {
                    //     console.warn('⚠️ Erreur lors du préchargement:', error);
                    // });
                });
            }
        });
        
        // Mesure du temps total jusqu'à la disponibilité de l'app
        performance.mark('app-ready');
        performance.measure('total-initialization', 'app-init-start', 'app-ready');
        
        const totalTime = performance.getEntriesByName('total-initialization')[0];
        logger.initialization.bootstrap.info(`⚡ Temps total d'initialisation: ${totalTime.duration.toFixed(2)}ms`);
        logger.initialization.bootstrap.info('✅ Application prête à l\'utilisation');
        
        // Analysis functions removed - use console cache size logs instead

        // Bootstrap initialization completed
        
    } catch (error) {
        logger.initialization.bootstrap.error("Échec de l'initialisation de l'application:", error);
        // Bootstrap initialization failed
        window.isReady = false;
        handleInitializationError(error.message);
    }
}

/**
 * Configure un écouteur d'événement pour le déchargement de la page
 */
function setupBeforeUnload() {
    window.addEventListener("beforeunload", function () {
        logger.initialization.bootstrap.debug("La page est sur le point de se recharger ou de se fermer.");
    });
}

/**
 * Attend que le DOM soit complètement chargé
 */
async function initializeDOMContent() {
    if (document.readyState === 'loading') {
        await new Promise(resolve => {
            document.addEventListener('DOMContentLoaded', () => {
                logger.initialization.bootstrap.debug("DOMContentLoaded fired.");
                resolve();
            });
        });
    }
}

/**
 * Masque l'overlay de chargement initial
 */
function hideOverlay() {
    const overlay = document.getElementById("overlay");
    if (overlay) {
        overlay.style.display = "none";
        logger.initialization.bootstrap.debug("Overlay hidden.");
    } else {
        logger.initialization.bootstrap.error("Element with ID 'overlay' not found.");
    }
}

/**
 * Wrapper pour l'initialisation de TomSelect
 */
async function initializeTomSelect() {
    try {
        rootPersonStore.initializeTomSelect();
        logger.initialization.bootstrap.debug('✅ TomSelect initialized');
    } catch (error) {
        logger.initialization.bootstrap.error('❌ Erreur lors de l\'initialisation de TomSelect:', error);
        throw error;
    }
}

/**
 * Wrapper pour l'initialisation de l'onboarding
 */
async function initializeOnboarding() {
    try {
        // L'OnboardingManager n'a pas besoin d'initialisation asynchrone pour le moment
        logger.initialization.bootstrap.debug('OnboardingManager prototype:', OnboardingManager.prototype);
        logger.initialization.bootstrap.debug('✅ Onboarding Manager initialized');
    } catch (error) {
        logger.initialization.bootstrap.error('❌ Erreur lors de l\'initialisation de l\'onboarding:', error);
        throw error;
    }
}

/**
 * Wrapper pour l'initialisation du gestionnaire de hero section
 */
async function initializeFanHeroManager() {
    try {
        await import('../tabs/fanChart/fanHeroManager.js');
        logger.initialization.bootstrap.debug('✅ FanHeroManager initialized');
    } catch (error) {
        logger.initialization.bootstrap.error('❌ Erreur lors de l\'initialisation de FanHeroManager:', error);
        throw error;
    }
}

async function initializeHeaderStateManager() {
    try {
        await import('./headerStateManager.js');
        logger.initialization.bootstrap.debug('✅ HeaderStateManager initialized');
    } catch (error) {
        logger.initialization.bootstrap.error('❌ Erreur lors de l\'initialisation de HeaderStateManager:', error);
    }
}

/**
 * Wrapper pour l'initialisation des écouteurs d'événements
 */
async function initializeEventListeners() {
    try {
        setupEventListeners();
        logger.initialization.bootstrap.debug('✅ Event listeners initialized');
    } catch (error) {
        logger.initialization.bootstrap.error('❌ Erreur lors de l\'initialisation des écouteurs:', error);
        throw error;
    }
}

/**
 * Wrapper pour l'initialisation du panneau de debug
 */
async function initializeDebugPanel() {
    try {
        await import('../utils/debug/DebugPanel.js');
        logger.initialization.bootstrap.debug('✅ Debug Panel initialized (Ctrl+Shift+D to toggle)');
    } catch (error) {
        logger.initialization.bootstrap.error('❌ Erreur lors de l\'initialisation du panneau de debug:', error);
        throw error;
    }
}


/**
 * Wrapper pour l'initialisation des tests PersonDetailsManager
 */
async function initializePersonDetailsTests() {
    try {
        // Import des tests uniquement en mode développement
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            const PersonDetailsTestsSimplified = await import('../tests/PersonDetailsManagerTestSimplified.js');
            const TestHelpers = await import('../utils/testHelpers.js');
            
            // Import du test de performance PersonStore
            await import('../tests/PersonStorePerformanceTest.js');
            
            // Exposer les fonctions de test globalement
            window.PersonDetailsManagerTest = PersonDetailsTestsSimplified;
            window.PersonDetailsTestHelpers = {
                ...TestHelpers,
                createTestLinks: TestHelpers.createTestLinks,
                quickTestPersonDetails: PersonDetailsTestsSimplified.quickTest
            };
            
            logger.initialization.bootstrap.debug('✅ PersonDetailsManager tests simplifiés (Bottom Drawer SEUL)');
            logger.initialization.bootstrap.debug('💡 Utilisez PersonDetailsTestHelpers.quickTestPersonDetails() pour un test rapide');
            logger.initialization.bootstrap.debug('🚀 PersonStore Performance Test disponible : window.runPersonStorePerformanceTest()');
        }
    } catch (error) {
        logger.initialization.bootstrap.warn('⚠️ PersonDetailsManager tests non disponibles:', error);
    }
}

/**
 * Wrapper pour l'initialisation de l'indicateur de tâches d'arrière-plan
 */
async function initializeBackgroundTaskUI() {
    try {
        initializeBackgroundTaskIndicator();
        logger.initialization.bootstrap.debug('✅ Background task indicator initialized');
    } catch (error) {
        logger.initialization.bootstrap.error('❌ Erreur lors de l\'initialisation de l\'indicateur de tâches:', error);
        throw error;
    }
}

/**
 * Démarre la capture continue de l'état de session (fichier, racine,
 * onglet, réglages de l'éventail) vers le localStorage.
 */
async function initializeSessionTracking() {
    try {
        const { default: sessionStateManager } = await import('./sessionStateManager.js');
        sessionStateManager.startTracking();
        logger.initialization.bootstrap.debug('✅ Session tracking initialized');
    } catch (error) {
        logger.initialization.bootstrap.error('❌ Erreur lors de l\'initialisation du suivi de session:', error);
        throw error;
    }
}

/**
 * Propose, via une bannière « Reprendre », de restaurer la dernière session
 * (l'authentification est déjà résolue à ce stade).
 */
async function initializeSessionRestore() {
    try {
        const { default: sessionStateManager } = await import('./sessionStateManager.js');
        sessionStateManager.offerRestore();
        logger.initialization.bootstrap.debug('✅ Session restore offer evaluated');
    } catch (error) {
        logger.initialization.bootstrap.error('❌ Erreur lors de la proposition de restauration de session:', error);
        throw error;
    }
}

/**
 * Gère les paramètres spécifiques dans l'URL
 */
async function handleUrlParameters() {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        const contexte = urlParams.get("contexte");

        // Gestion des options d'export (défaut vs admin)
        const adminExportOptions = document.getElementById('admin-export-options');
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

        if (isLocalhost || contexte === "admin") {
            // Localhost ou mode admin : afficher les options PNG/SVG en plus du PDF
            if (adminExportOptions) adminExportOptions.style.display = "";
            logger.initialization.bootstrap.debug(`🔓 Options export admin visibles (${isLocalhost ? 'localhost' : 'admin'})`);
        } else {
            // Par défaut : masquer les options admin (seul le PDF est visible)
            if (adminExportOptions) adminExportOptions.style.display = "none";
        }

        // Gestion du mode demo (masquer aussi show-missing)
        if (contexte === "demo") {
            const showMissing = document.querySelector("#show-missing");
            if (showMissing) showMissing.closest(".col").style.display = "none";
        }

        logger.initialization.bootstrap.debug('✅ URL parameters processed');
    } catch (error) {
        logger.initialization.bootstrap.error('❌ Erreur lors du traitement des paramètres URL:', error);
        throw error;
    }
}